import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { FileUpload } from '@/components/shared/components/forms/FileUpload';
import { API_CONFIG } from '@/config';
import { useState, useEffect } from 'react';

export interface PersonalInfoFormData {
  fullName: string;
  description: string;
  phoneNumber: string;
  photoUrl: string;
  resumeUrl: string;
  isResumePublic: boolean;
  forSelf: boolean;
  createdAt?: number;
  updatedAt?: number;
}

interface PersonalInfoFormProps {
  formData: PersonalInfoFormData;
  onChange: (data: PersonalInfoFormData) => void;
  onSubmit: (e: React.FormEvent) => void;
  submitLabel?: string;
}

export function PersonalInfoForm({
  formData,
  onChange,
  onSubmit,
  submitLabel = 'حفظ',
}: PersonalInfoFormProps) {
  const [error, setError] = useState<string | null>(null);
  const [isValid, setIsValid] = useState(false);

  // Validate phone number format
  const isValidPhoneNumber = (phone: string | undefined): boolean => {
    if (!phone) return false;
    const saudiPattern = /^009665\d{8}$/;  // Saudi format
    const kuwaitPattern = /^00965[569]\d{7}$/;  // Kuwait format
    return saudiPattern.test(phone.trim()) || kuwaitPattern.test(phone.trim());
  };

  // Validate all form fields
  const validateForm = () => {
    const isNameValid = formData?.fullName?.trim()?.length > 0;
    const isDescriptionValid = formData?.description?.trim()?.length > 0;
    const isPhoneValid = isValidPhoneNumber(formData?.phoneNumber);

    setIsValid(isNameValid && isDescriptionValid && isPhoneValid);
    
    if (!isNameValid) {
      setError('الاسم مطلوب');
      return false;
    }
    if (!isDescriptionValid) {
      setError('الوصف مطلوب');
      return false;
    }
    if (!isPhoneValid) {
      setError('رقم الهاتف غير صحيح. يجب أن يكون بالصيغة الدولية:\n السعودية: 009665xxxxxxxx\n الكويت: 00965xxxxxxx');
      return false;
    }

    setError(null);
    return true;
  };

  // Validate form on any field change
  useEffect(() => {
    validateForm();
  }, [formData?.fullName, formData?.description, formData?.phoneNumber]);

  const handleChange = (field: keyof PersonalInfoFormData, value: any) => {
    onChange({ ...formData, [field]: value });
  };

  const handleSubmit = (e: React.MouseEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(e as any);
    }
  };

  return (
    <form className="space-y-6 mx-4">
      <div className="space-y-4">
        <Label>نوع الطلب</Label>
        <RadioGroup
          value={formData?.forSelf ? 'self' : 'other'}
          onValueChange={(value) => handleChange('forSelf', value === 'self')}
          className="flex flex-col gap-2"
        >
          <div className="flex items-center justify-end gap-2">
            <Label htmlFor="self" className="cursor-pointer">
              أقوم بتعبئة معلوماتي الشخصية
            </Label>
            <RadioGroupItem value="self" id="self" />
          </div>
          <div className="flex items-center justify-end gap-2">
            <Label htmlFor="other" className="cursor-pointer">
              أقوم بتعبئة معلومات شخص آخر
            </Label>
            <RadioGroupItem value="other" id="other" />
          </div>
        </RadioGroup>
      </div>

      <div className="space-y-2">
        <Label htmlFor="fullName">الاسم الرباعي</Label>
        <Input
          id="fullName"
          required
          value={formData?.fullName || ''}
          onChange={(e) => handleChange('fullName', e.target.value)}
          placeholder="أدخل الاسم الرباعي"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">نبذة مختصرة</Label>
        <Textarea
          id="description"
          required
          value={formData?.description || ''}
          onChange={(e) => handleChange('description', e.target.value)}
          placeholder="اكتب نبذة مختصرة"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="phoneNumber">رقم الجوال *</Label>
        <Input
          id="phoneNumber"
          required
          value={formData?.phoneNumber || ''}
          onChange={(e) => handleChange('phoneNumber', e.target.value)}
          placeholder="رقم الجوال *"
        />
      </div>

      <div className="space-y-2">
        <Label>الصورة الشخصية (اختياري)</Label>
        <FileUpload
          type="image"
          value={formData?.photoUrl ? API_CONFIG.BASE_URL + '/' + formData.photoUrl : ""}
          onChange={(value) => handleChange('photoUrl', value)}
          label="الصورة الشخصية"
        />
      </div>

      <div className="space-y-4">
        <div className="space-y-2">
          <Label>السيرة الذاتية PDF (اختياري)</Label>
          <FileUpload
            type="pdf"
            value={formData?.resumeUrl ? API_CONFIG.BASE_URL + '/' + formData.resumeUrl : ""}
            onChange={(value) => handleChange('resumeUrl', value)}
            label="ملف السيرة الذاتية"
          />
        </div>

        {formData?.resumeUrl && (
          <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
            <Switch
              checked={formData?.isResumePublic || false}
              onCheckedChange={(checked) => handleChange('isResumePublic', checked)}
            />
            <Label className="text-sm text-gray-700">
              جعل السيرة الذاتية متاحة للجميع
            </Label>
          </div>
        )}

        {error && (
          <p className="text-sm text-red-500 mt-4 text-center">{error}</p>
        )}
      </div>

      <Button
        type="submit"
        className="w-full hover:bg-secondary"
        onClick={handleSubmit}
        disabled={!isValid}
      >
        {submitLabel}
      </Button>
    </form>
  );
}